/**
 * Slowmode (2026-10-29): when this person may send again in a channel. The
 * server enforces it (messages.service, `rate_limit_per_user`) and skips
 * Manage Messages, Manage Channels and Bypass Slowmode holders, and the
 * console's `slowmode` flag turns it off; the composer shows the countdown
 * from the person's own last message here (after a reload too) or the wait
 * a refused send was told.
 */
import { MessageType, Permissions, type Channel, type Snowflake } from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { raidFloor } from '../hub/raidSlowmode';
import { flagOn, useConfig, useFlag } from './config';
import { useData } from './data';
import type { DataState } from './reducer';
import { can, channelPermissions } from './selectors';

/** channel → when a send is allowed again (unix ms), from sends here and the server's 429s. */
export const useSlowmodeUntil = create<Record<Snowflake, number>>(() => ({}));

/** The bits the server's slowmode skips. */
const BYPASS = [
  Permissions.BYPASS_SLOWMODE,
  Permissions.MANAGE_MESSAGES,
  Permissions.MANAGE_CHANNELS,
];

/** The channel's slowmode in seconds, 0 when off (or the console turned slowmode off). */
export function slowmodeOf(ch: Channel | undefined): number {
  if (!ch?.guild_id || !flagOn('slowmode')) return 0;
  return ch.rate_limit_per_user ?? 0;
}

/** True when slowmode doesn't apply to you in `ch` (your permissions there). */
export function slowmodeExempt(d: DataState, ch: Channel): boolean {
  const p = channelPermissions(d, ch);
  return BYPASS.some((b) => can(p, b));
}

/** Your last own message here (sent or a reply), as unix ms; 0 when none is loaded. */
function lastOwnSend(d: DataState, channelId: Snowflake): number {
  const cm = d.messages[channelId];
  const me = d.me?.id;
  if (!cm || !me) return 0;
  for (let i = cm.ids.length - 1, n = 0; i >= 0 && n < 50; i--, n++) {
    const m = cm.byId[cm.ids[i]!];
    if (m && m.author.id === me && (m.type === MessageType.DEFAULT || m.type === MessageType.REPLY))
      return Date.parse(m.timestamp) || 0;
  }
  return 0;
}

/** A send went out in `ch`: the countdown starts. */
export function noteSlowmodeSend(ch: Channel): void {
  const d = useData.getState();
  const own = slowmodeOf(ch);
  // Console v2: raid mode's floor (Manage Messages exempt), as the server
  const rate = Math.max(
    own > 0 && !slowmodeExempt(d, ch) ? own : 0,
    raidFloor(d, ch, useConfig.getState().config?.raid),
  );
  if (rate <= 0) return;
  useSlowmodeUntil.setState({ [ch.id]: Date.now() + rate * 1000 });
}

/** The server said to wait `seconds` in this channel (a 429). */
export function noteSlowmodeWait(channelId: Snowflake, seconds: number): void {
  const until = Date.now() + Math.max(0, seconds) * 1000;
  if ((useSlowmodeUntil.getState()[channelId] ?? 0) < until)
    useSlowmodeUntil.setState({ [channelId]: until });
}

/**
 * The composer's view of slowmode in `ch`: its length (0: off), whether you
 * are exempt, and the seconds left before you may send (ticking).
 */
export function useSlowmode(ch: Channel): { rate: number; exempt: boolean; left: number } {
  const on = useFlag('slowmode');
  const own = on && ch.guild_id ? (ch.rate_limit_per_user ?? 0) : 0;
  const ownExempt = useData((d) => own > 0 && slowmodeExempt(d, ch));
  // Console v2: raid mode's floor in public text channels (the effective delay)
  const raid = useConfig((c) => c.config?.raid);
  const floor = useData((d) => raidFloor(d, ch, raid));
  const rate = Math.max(own > 0 && !ownExempt ? own : 0, floor) || own;
  const exempt = rate > 0 && ownExempt && floor === 0;
  const last = useData((d) => (rate > 0 && !exempt ? lastOwnSend(d, ch.id) : 0));
  const noted = useSlowmodeUntil((s) => s[ch.id] ?? 0);
  const end = rate > 0 && !exempt ? Math.max(noted, last ? last + rate * 1000 : 0) : 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!end) return;
    const tick = () => setNow(Date.now());
    const first = setTimeout(tick, 0);
    const h = setInterval(() => {
      tick();
      if (Date.now() >= end) clearInterval(h);
    }, 250);
    return () => {
      clearTimeout(first);
      clearInterval(h);
    };
  }, [end]);
  return { rate, exempt, left: end ? Math.max(0, Math.ceil((end - now) / 1000)) : 0 };
}

/** "0:07", "4:30", "1:00:00": a countdown as Discord writes it. */
export function countdownText(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** Slowmode lengths as words: "Off", "5 seconds", "2 minutes", "6 hours" (i18n keys `sm.*`). */
export function slowmodeLabel(
  t: (k: string, v?: Record<string, number>) => string,
  s: number,
): string {
  if (s <= 0) return t('sm.off');
  if (s < 60) return t('sm.s', { n: s });
  if (s < 3600) return t('sm.m', { n: Math.round(s / 60) });
  return t('sm.h', { n: Math.round(s / 3600) });
}
